import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { Ad } from "@models/Ad.model";
import { fuzzyMatch } from "@/shared/utils/fuzzyMatch";

// Re-exported so existing importers keep working; the implementation moved to shared so the
// Moti Store searches with the same matcher (MPN-1575).
export { fuzzyMatch };

export type SearchTreeResult = {
    visibleFolderIds: Set<number>;
    autoExpandFolderIds: Set<number>;
    visibleAdIdsByFolder: Map<number, Set<number>>;
};

export const EMPTY_SEARCH: SearchTreeResult = {
    visibleFolderIds: new Set(),
    autoExpandFolderIds: new Set(),
    visibleAdIdsByFolder: new Map(),
};

export function buildAllContentSearchTree(nodes: ContentCatalogFolder[], query: string): SearchTreeResult {
    const visibleFolderIds = new Set<number>();
    const autoExpandFolderIds = new Set<number>();
    const visibleAdIdsByFolder = new Map<number, Set<number>>();
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
        return { visibleFolderIds, autoExpandFolderIds, visibleAdIdsByFolder };
    }

    const walk = (folder: ContentCatalogFolder): boolean => {
        const folderMatches = fuzzyMatch(folder.name, normalizedQuery);

        const matchingAdIds = new Set<number>();
        (folder.ads || []).forEach((ad: Ad) => {
            const adLabel = ad.name || `Ad ${ad.id}`;
            if (fuzzyMatch(adLabel, normalizedQuery)) {
                matchingAdIds.add(ad.id);
            }
        });

        if (matchingAdIds.size > 0) {
            visibleAdIdsByFolder.set(folder.id, matchingAdIds);
            autoExpandFolderIds.add(folder.id);
        }

        let descendantMatches = false;
        (folder.children || []).forEach((child) => {
            const childMatches = walk(child);
            if (childMatches) {
                descendantMatches = true;
                autoExpandFolderIds.add(folder.id);
            }
        });

        const hasMatch = folderMatches || matchingAdIds.size > 0 || descendantMatches;
        if (hasMatch) visibleFolderIds.add(folder.id);

        return hasMatch;
    };

    nodes.forEach((rootNode) => {
        walk(rootNode);
    });

    return { visibleFolderIds, autoExpandFolderIds, visibleAdIdsByFolder };
}
